import type {
  ComponentPropsWithoutRef,
  ElementType,
  ReactNode,
} from 'react';

import { cn } from '../lib/cn.js';

type NavItemOwnProps = {
  icon: ReactNode;
  label: string;
  showStatusDot?: boolean;
  // Same shape as showStatusDot, but with a subtle accent glow. Used to
  // signal unread activity rather than a static "configured" status.
  // Wins over showStatusDot when both are set.
  pulseDot?: boolean;
  isActive?: boolean;
};

export type NavItemProps<E extends ElementType = 'a'> = NavItemOwnProps & {
  as?: E;
} & Omit<ComponentPropsWithoutRef<E>, keyof NavItemOwnProps | 'as'>;

/*
 * Sidebar nav row: leading icon, label, optional trailing status dot.
 * Router-agnostic via the polymorphic `as` prop (default `<a>`); the active
 * state is fed in via `isActive` rather than computed here so the same shape
 * works with NavLink, anchors, or buttons.
 */
export function NavItem<E extends ElementType = 'a'>({
  as,
  icon,
  label,
  showStatusDot = false,
  pulseDot = false,
  isActive = false,
  className,
  ...rest
}: NavItemProps<E>): JSX.Element {
  const Component = (as ?? 'a') as ElementType;
  // pulseDot wins; showStatusDot is the static fallback so existing
  // consumers keep their behaviour.
  const dotKind: 'pulse' | 'static' | 'none' = pulseDot
    ? 'pulse'
    : showStatusDot
      ? 'static'
      : 'none';
  return (
    <Component
      className={cn(
        'group relative flex items-center gap-2.5 rounded-sm px-3 py-2 text-sm',
        'border-l-2 border-transparent transition-colors duration-150',
        isActive
          ? 'border-l-accent bg-accent-bg text-accent'
          : 'text-text-muted hover:bg-surface-2 hover:text-text',
        className as string | undefined,
      )}
      {...rest}
    >
      <span className="text-current">{icon}</span>
      <span className="flex-1">{label}</span>
      {dotKind !== 'none' && (
        <span
          aria-hidden
          className={cn(
            'h-1.5 w-1.5 rounded-full bg-accent',
            dotKind === 'pulse' && 'motion-safe:animate-glow',
          )}
        />
      )}
    </Component>
  );
}
